import { type FC, useMemo, useState } from 'react';

import { EMortgageAnnotationHeightPercent } from 'shared/enums/mortgageAnnotationHeightPercent';
import { splitPhraseIntoTwoLines } from 'shared/utils/splitPhraseIntoTwoLines';

import { AllSpaceText } from '../AllSpaceText';

import styles from './MortgageAnnotations.css';

const MIN_LETTER_SPACING = -1.5;

export type SplitTextBehavior =
  /** Разделить текст на строки автоматически, если он не умещается в одну строку после подсчётов (по умолчанию) */
  | 'auto'
  /** Разделить текст на 2 строки без подсчётов, аналогично `true` */
  | 'force'
  /** Разделить текст на 2 строки без подсчётов, аналогично `'force'` */
  | boolean;

export type MortgageAnnotationsProps = {
  mortgageWarning: string | undefined;
  splitMortgageWarning?: SplitTextBehavior;
  mortgageTermsWarning: string | undefined;
  splitMortgageTermsWarning?: SplitTextBehavior;
  height: number;
};

export const MortgageAnnotations: FC<MortgageAnnotationsProps> = ({
  mortgageWarning,
  splitMortgageWarning = 'auto',
  mortgageTermsWarning,
  splitMortgageTermsWarning = 'auto',
  height,
}) => {
  const [splitMortgageWarningForce, setSplitMortgageWarningForce] = useState(false);
  const [splitMortgageTermsWarningForce, setSplitMortgageTermsWarningForce] = useState(false);

  const mortgageWarningView = useMemo(() => {
    if (!mortgageWarning) {
      return null;
    }

    if (
      splitMortgageWarning === true ||
      splitMortgageWarning === 'force' ||
      (splitMortgageWarning === 'auto' && splitMortgageWarningForce)
    ) {
      return splitPhraseIntoTwoLines(mortgageWarning).map((text, index) => (
        <AllSpaceText height={height} heightPercent={EMortgageAnnotationHeightPercent.Half} key={index} text={text} />
      ));
    }

    return (
      <AllSpaceText
        height={height}
        heightPercent={EMortgageAnnotationHeightPercent.Default}
        text={mortgageWarning}
        onAllSpaceCalculated={({ letterSpacing }) => {
          let demount = false;

          if (splitMortgageWarning === 'auto' && letterSpacing < MIN_LETTER_SPACING) {
            setSplitMortgageWarningForce(true);

            demount = true;
          }

          return demount;
        }}
      />
    );
  }, [height, mortgageWarning, splitMortgageWarning, splitMortgageWarningForce]);

  const mortgageTermsWarningView = useMemo(() => {
    if (!mortgageTermsWarning) {
      return null;
    }

    if (
      splitMortgageTermsWarning === true ||
      splitMortgageTermsWarning === 'force' ||
      (splitMortgageTermsWarning === 'auto' && splitMortgageTermsWarningForce)
    ) {
      return splitPhraseIntoTwoLines(mortgageTermsWarning).map((text, index) => (
        <AllSpaceText height={height} heightPercent={EMortgageAnnotationHeightPercent.Half} key={index} text={text} />
      ));
    }

    return (
      <AllSpaceText
        height={height}
        heightPercent={EMortgageAnnotationHeightPercent.Default}
        text={mortgageTermsWarning}
        onAllSpaceCalculated={({ letterSpacing }) => {
          let demount = false;

          if (splitMortgageTermsWarning === 'auto' && letterSpacing < MIN_LETTER_SPACING) {
            setSplitMortgageTermsWarningForce(true);

            demount = true;
          }

          return demount;
        }}
      />
    );
  }, [height, mortgageTermsWarning, splitMortgageTermsWarning, splitMortgageTermsWarningForce]);

  if (!mortgageWarningView && !mortgageTermsWarningView) {
    return null;
  }

  return (
    <div className={styles['container']}>
      {mortgageWarningView}
      {mortgageTermsWarningView}
    </div>
  );
};
